/* eslint-disable react/no-danger */
import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Blocks from '../blocks/Blocks';
import StyledButton from '../buttons/StyledButton';
import DrawsSection from '../items/DrawsSection';
import CompleteFormEntry from '../partials/CompleteFormEntry';
import DrawsWinners from '../partials/DrawsWinners';
import PageTitle from '../partials/PageTitle';

import styles from '../../../styles/pages/draws-default.module.scss';

interface DrawsPageProps {
    page: DrawsPage | null;
}

function DrawsDefaultPage({ page }: DrawsPageProps) {
    const url = useUrlGenerator();
    const themeClassName = useThemeClassName(styles);
    const user = useUser();

    const {
        showChancesButton = true,
        title: pageTitle = null,
        description = null,
        sections = [],
        blocks = [],
    } = (page as DrawsPage) || {};

    const draws = useMemo(
        () => sections.reduce((all, { draws: sectionDraws }) => [...all, ...sectionDraws], []),
        [sections],
    );

    const beforeBlocks =
        blocks !== null
            ? blocks.filter(({ placement = 'before' }) => placement === 'before')
            : null;

    const afterBlocks =
        blocks !== null
            ? blocks.filter(({ placement = 'before' }) => (placement || 'after') === 'after')
            : null;

    return (
        <div className={classNames([styles.container, themeClassName])}>
            <CompleteFormEntry className={styles.completeFormEntry} />
            <PageTitle title={pageTitle} className={styles.title} />
            {description !== null ? (
                <div
                    className={styles.description}
                    dangerouslySetInnerHTML={{ __html: description }}
                />
            ) : null}
            {beforeBlocks !== null && beforeBlocks.length > 0 ? (
                <div className={styles.pageBlocks}>
                    <Blocks
                        blockClassName={styles.block}
                        blockTypeClassNames={styles}
                        blocks={beforeBlocks}
                    />
                </div>
            ) : null}
            {sections.map((section, drawI) => (
                <DrawsSection
                    key={`section-${section.id}`}
                    section={section}
                    className={styles.section}
                    datesClassName={classNames([
                        styles.drawDates,
                        {
                            [styles.first]: drawI === 0,
                        },
                    ])}
                />
            ))}
            {afterBlocks !== null && afterBlocks.length > 0 ? (
                <div className={styles.pageBlocks}>
                    <Blocks
                        blockClassName={styles.block}
                        blockTypeClassNames={styles}
                        blocks={afterBlocks}
                    />
                </div>
            ) : null}
            {user !== null && showChancesButton ? (
                <div className={styles.buttons}>
                    <StyledButton colored className={styles.button} href={url('account')}>
                        <FormattedMessage
                            defaultMessage="See my chances"
                            description="Button label"
                        />
                    </StyledButton>
                </div>
            ) : null}
            <DrawsWinners draws={draws} className={styles.winnersContainer} />
        </div>
    );
}

export default DrawsDefaultPage;
